<template>
  <!-- Count of PATIENTS with a directive awaiting action — a document pending
       authorization confirmation, a record pending sign-off, or an active
       record past its re-authorization date. Nothing else moves this number.
       Hidden while loading, on fetch failure, and at zero — the badge is a
       glanceable cue, never a blocker.

       The number alone is ambiguous (records? patients? directives?) and
       `title` never appears on touch, so the meaning is carried by an
       aria-label for assistive tech rather than by hover alone.

       This count is ORG-WIDE, so it is shown only to an org-scoped session
       that is not currently inside one patient's chart — see `showBadge`. -->
  <span
    v-if="showBadge && attentionPatients > 0"
    class="dir-nav-badge"
    role="status"
    :aria-label="badgeLabel"
    :title="badgeLabel"
    >{{ attentionPatients }}</span
  >
</template>

<script>
import { mapGetters } from "vuex";
import EventBus from "@/event-bus";
import { DIRECTIVES_CHANGED } from "@/constants/directive-events";
import { getDirectiveCounts, DIRECTIVE_STATUS } from "@/api/directivesApi";
import { usePrivilegesStore } from "@/stores/privileges";
import { canSeeOrgDirectiveCounts } from "@/utils/directivesScope";
import { getDirectiveActorId } from "@/utils/directivesSession";

export default {
  name: "directives-nav-badge",
  emits: ["state-change"],
  data() {
    // `fetching` is deliberately not reactive — it only guards overlapping
    // requests and nothing renders from it.
    return { counts: {}, attentionPatients: 0, fetching: false, privilegesStore: usePrivilegesStore() };
  },
  computed: {
    ...mapGetters("login", { allData: "getAllData" }),
    /** Hidden inside a patient's chart, and for any self-scoped session. */
    showBadge() {
      return canSeeOrgDirectiveCounts(this.allData, this.privilegesStore);
    },
    accountnumber() {
      return this.allData?.patient?.externId2 || this.allData?.manager?.externid2 || "";
    },
    activeCount() {
      return Number(this.counts[DIRECTIVE_STATUS.ACTIVE] || 0);
    },
    badgeLabel() {
      const n = this.attentionPatients;
      return `${n} ${n === 1 ? "patient" : "patients"} with directives awaiting authorization, sign-off, or re-authorization`;
    },
    // "Pending" for nav-highlight purposes covers both in-flight authorization
    // states - awaiting a signature or awaiting notarization - either one means
    // there's no active directive yet but something's in progress.
    pendingCount() {
      return (
        Number(this.counts[DIRECTIVE_STATUS.PENDING_REVIEW] || 0) +
        Number(this.counts[DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION] || 0)
      );
    },
    directiveState() {
      if (this.activeCount > 0) return "active";
      if (this.pendingCount > 0) return "pending";
      return "none";
    },
  },
  watch: {
    directiveState: {
      immediate: true,
      handler(v) {
        this.$emit("state-change", v);
      },
    },
    // Entering or exiting a patient's chart does not remount the nav, and
    // privileges load asynchronously after login. Re-evaluate on both so the
    // badge appears and disappears without a page reload.
    showBadge: {
      immediate: true,
      handler() {
        this.refresh();
      },
    },
  },
  mounted() {
    // No initial refresh() here — the `showBadge` watcher above is `immediate`
    // and already ran it, and a second call would race the first.
    //
    // Signing, re-authorizing, revoking or deleting a record all change this
    // count, and none of them remount the nav. One small aggregate query per
    // change is cheaper than leaving a stale number on screen.
    EventBus.on(DIRECTIVES_CHANGED, this.refresh);
  },
  beforeUnmount() {
    EventBus.off(DIRECTIVES_CHANGED, this.refresh);
  },
  methods: {
    async refresh() {
      // Don't fetch a number we will not show. Clearing rather than keeping a
      // stale count also drops the nav link's colour back to neutral via
      // `directiveState`, so nothing about the org's state leaks through the
      // styling once the badge itself is hidden.
      if (!this.showBadge) {
        this.counts = {};
        this.attentionPatients = 0;
        return;
      }
      if (!this.accountnumber) return;
      // In flight already — the change that triggered this is included either
      // way, so a second overlapping request would only race the first.
      if (this.fetching) return;
      this.fetching = true;
      try {
        const { counts, attentionPatients } = await getDirectiveCounts({
          accountnumber: this.accountnumber,
          actorId: getDirectiveActorId(this.allData),
        });
        this.counts = counts;
        this.attentionPatients = attentionPatients;
      } catch (e) {
        console.warn("directive counts fetch failed", e);
      } finally {
        this.fetching = false;
      }
    },
  },
};
</script>

<style scoped>
.dir-nav-badge {
  display: inline-block;
  min-width: 1.25rem;
  margin-left: 0.35rem;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.1rem;
  text-align: center;
  color: var(--eh-on-dark, #fff);
  background: var(--eh-copper, #e65100);
  vertical-align: middle;
}
</style>
